/* eslint-disable no-nested-ternary */
import React, { useEffect } from 'react';

// material-ui
import { Autocomplete, Box, Checkbox, FormControlLabel, FormGroup, Grid, TextField, Typography, Switch } from '@mui/material';
import { useTheme } from '@mui/styles';
import { LoadingButton } from '@mui/lab';

import { useDispatch, useSelector } from 'react-redux';
import { getSpecificCustomer, selectCustomerById } from 'store/customers/customersSlice';
import { RootState } from 'store';
import {
  getCustomerProduct,
  selectActiveProducts,
  selectCustomersOrderProductsWithSort
} from 'store/customersProducts/customersProductsSlice';

// project imports
import config from 'config';
import SubCard from 'ui-component/cards/SubCard';
import Logo from 'ui-component/Logo';
import MyChip from 'ui-component/extended/Chip';

// Locale
import { format } from 'date-fns';
import { enUS, he } from 'date-fns/locale';
import { FormattedMessage } from 'react-intl';

// types
import DocumentHeaderPreview from '../document_header_preview/DocumentHeaderPreview';
import DocumentClientDetails from '../document_header_preview/DocumentClientDetails';
import { ExtendedDocument } from '../../../../../types/DocumentsTypes/Document';
import {useCreateCustomerDocument} from "../../../../../hooks/customer/documents.hooks";
import Swal from "sweetalert2";
import {createDocument, visitStates} from "../../../../../types/DocumentsTypes/utils";
import JSDB from "../../../../../fromKotlin/nk";
import getCustomerProductsPrices from "../../../../../api/customer/products_prices.api";
import {dateObjectToString} from "../../../../../fromKotlin/nkutils";
import {getClientsDaily} from "../../../../../api/customer/documents.api";
import IItem from "../../../../../types/product_delivery";
import CustomerProductsList from 'views/customer/profile/products/CustomerProductsList';
import VisitPreview from './VisitPreview';
import MyDatePicker from 'ui-component/MyDatePicker';


export const documentStatesArr: visitStates[] = [
  { label: "ביקור כללי", value: 0 },
  { label: "ביצעתי הזמנה", value: 1 },
  { label: "ביצעתי מכירה", value: 2 },
  { label: "לא צריך סחורה", value: 3 },
  { label: "לקחתי החזרות", value: 4 },
  { label: "לקחתי תשלום", value: 5 },
  { label: "הוספתי לקוח", value: 6 },
  { label: "יש מתקן בנקודה", value: 7 },
  { label: "האם הנקודה מוגדרת", value: 8 }
];

// Remove VisitProduct and product from VisitDocument, use products: IItem[]
interface VisitDocument extends ExtendedDocument {
  states: string[];
  products: IItem[];
  visitDetails?: string;
  payment?: number;
  comment?: string;
}

import CustomerProductsPreviewList from '../../products/CustomerProductsPreviewList';

export const NewVisitActions = ({upid, internalEdit, setInternalEdit}: {upid?: any, internalEdit?: any, setInternalEdit?: any}) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [allProducts, setAllProducts] = React.useState<IItem[]>([]);
  const [showTableMode, setShowTableMode] = React.useState(true);
  React.useEffect(() => {
    const genProds = async () => {
      const ps = await getCustomerProductsPrices(upid, dateObjectToString(internalEdit?.date ?? new Date()), false);
      setAllProducts(ps);
    };
    genProds();
  }, [dispatch, upid, internalEdit?.date]);

  // Handler for product add/edit
  const onProductChange = (product: IItem, idx?: number) => {
    setInternalEdit((prev: VisitDocument) => {
      const products = prev?.products ? [...prev.products] : [];
      if (typeof idx === 'number') {
        products[idx] = product;
      } else {
        // Add or update by id+notePlacement
        let updated = false;
        for (let i = 0; i < products.length; i++) {
          if (products[i].id === product.id && products[i].notePlacement === product.notePlacement) {
            products[i] = product;
            updated = true;
            break;
          }
        }
        if (!updated) products.push(product);
      }
      return { ...prev, products };
    });
  };


  const clearProducts = React.useCallback(() => {
    setInternalEdit((prev: VisitDocument) => ({
      ...prev,
      products: prev.products.map((p) => ({...p, quantity: 0, quantityReturns: 0}))
    }));
  }, [setInternalEdit]);
  return (
    <Grid item xs={12}>
      <SubCard
        sx={{
          bgcolor: theme.palette.mode === 'dark' ? theme.palette.dark.main : theme.palette.primary.light
        }}
      >
        <FormControlLabel
          control={<Switch checked={showTableMode} onChange={() => setShowTableMode((prev) => !prev)} />}
          label={showTableMode ? 'מצב טבלה' : 'מצב רגיל'}
          sx={{ mb: 2 }}
        />
        {showTableMode ? (
          <Grid container sx={{ marginBottom: '10px' }}>
            <CustomerProductsPreviewList
              products={internalEdit?.products || []}
              onProductChange={onProductChange}
              id={upid}
              date={internalEdit?.date || new Date()}
            />
          </Grid>
        ) : (
          <CustomerProductsList
            products={allProducts|| []}
            onProductChange={onProductChange}
            clearFunc={clearProducts}
            withReturns={true}
            getAllProducts={false}
            setGetAllProducts={() => {}}
            sup={false}
          />
        )}
        <Grid container sx={{
          width: '100%',
          marginBottom: '10px',
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
        }}>
          <Grid sx={{ width: '100%' }}>
            <FormGroup sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 0.5
            }}>
              {documentStatesArr.map((s) => (
                <FormControlLabel
                  key={s.value}
                  control={<Checkbox
                    checked={internalEdit?.states?.includes(s.value) || internalEdit?.states?.includes(s.label) || false}
                    onChange={(e) => {
                      if (internalEdit) {
                        if (e.target.checked) {
                          setInternalEdit((prev: VisitDocument) => ({ ...prev, states: prev?.states?.length ? [...prev.states, s.value] : [s.value] }));
                        } else {
                          setInternalEdit((prev: VisitDocument) => ({ ...prev, states: prev?.states?.length ? prev.states.filter((state) => state !== s.value) : [] }));
                        }
                      }
                    }}
                    name={String(s.value)}
                    color="primary"
                  />}
                  label={s.label}
                />
              ))}
            </FormGroup>
          </Grid>
          <TextField
            sx={{ width: '100%' }}
            label="פרט ביקור"
            multiline
            rows={4}
            value={internalEdit?.comment ?? ''}
            onChange={(e) => {
              setInternalEdit((prev: VisitDocument) => ({ ...prev, comment: e.target.value }));
            }}
          />
          <TextField
            sx={{ width: 150 }}
            label="סכום תשלום"
            type="number"
            value={internalEdit?.payment ?? 0}
            onChange={(e) => {
              setInternalEdit((prev: VisitDocument) => ({ ...prev, payment: Number(e.target.value) }));
            }}
          />
        </Grid>
      </SubCard>
    </Grid>
  );
};

const NewVisit = ({ upid }: { upid: string }) => {
  const dispatch = useDispatch();
  const customer = useSelector((state: RootState) => selectCustomerById(state, upid));
  const [document, setDocument] = React.useState<VisitDocument>({ products: [], states: [], date: new Date(), issueDate: new Date() } as any);
  const createCustomerDocument = useCreateCustomerDocument(upid);
  const theme = useTheme();
  React.useEffect(() => {
    if (!customer) {
      dispatch(getSpecificCustomer(Number(upid)));
      return;
    }
  }, [dispatch, customer, upid]);
  return (

        createCustomerDocument.isSuccess && createCustomerDocument.data ? (
                <Grid item xs={12} md={12}>
                  <VisitPreview editting={false} {...(createCustomerDocument.data as any)} />
                </Grid>
            ) :
            <SubCard
                darkTitle
                title={<Typography fontWeight='bold'>תעודת ביקור</Typography>}
                secondary={<Logo width={50}/>}
            >
              <Grid container spacing={2}>
                <DocumentHeaderPreview/>
                <Grid item xs={12}>
                  <Grid container>
                    {customer &&
                        <DocumentClientDetails
                            address={`${customer?.street} ${customer?.streetNumber}, ${customer?.city}`}
                            name={customer?.name}
                            businessName={customer?.businessName} businessId={customer?.businessId}
                            phone={customer?.phone} email={customer?.email} id={customer?.id}
                            external_id={customer?.externalId}/>
                    }
                    <Grid item sm={6}>
                      <Grid container>
                        <Typography variant='subtitle1' fontWeight='bold'>פרטי ביקור</Typography>
                        <Grid item xs={12}>
                          <Grid container>
                            <Grid item xs={4}>
                              <Typography variant='body2'><FormattedMessage id='Date'/>:</Typography>
                            </Grid>
                            <Grid item xs={8}>
                              <MyDatePicker
                                date={document?.date ?? new Date()}
                                autoFocus={false}
                                setDate={(date) => setDocument((prev: VisitDocument) => ({ ...prev, date }))}
                                isLoading={undefined}
                              />
                            </Grid>
                            <Grid item xs={4}>
                              <Typography variant='body2'>תאריך הנפקה:</Typography>
                            </Grid>
                            <Grid item xs={8}>
                              <Typography variant='body2'>
                                {format(document?.issueDate ?? new Date(), 'PPPPp', {
                                  locale: config.i18n === 'he' ? he : enUS
                                })}
                              </Typography>
                            </Grid>
                            <Grid item xs={4}>
                              <Typography variant='body2'><FormattedMessage id='Status'/>:</Typography>
                            </Grid>
                            <Grid item xs={8}>
                              <MyChip
                                  label={<FormattedMessage id={'pending_approval'}/>}
                                  variant='outlined'
                                  size='small'
                                  chipcolor={'pending_approval'}
                              />
                            </Grid>
                            <>
                              <Grid item xs={4}>
                                <Typography variant='body2'>סוכן:</Typography>
                              </Grid>
                              <Grid item xs={8}>
                                <Typography variant='body2'>{customer?.agent ?? ''}</Typography>
                              </Grid>
                            </>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
                <NewVisitActions upid={upid} internalEdit={document} setInternalEdit={setDocument}/>
                <Grid item xs={12} sm={12}>
                  <Box
                      sx={{
                        display: 'flex',
                        flex: 1,
                        flexDirection: 'row',
                        justifyContent: 'space-evenly',
                      }}>
                    <LoadingButton
                        onClick={() => {
                          Swal.fire({
                            title: 'האם תרצה להפיק ביקור',
                            icon: 'warning',
                            showCancelButton: true,
                            confirmButtonColor: theme.palette.primary.dark,
                            cancelButtonColor: theme.palette.error.light,
                            confirmButtonText: 'הפק ביקור',
                            cancelButtonText: 'בטל'
                          }).then((result) => {
                            if (result.isConfirmed) {
                              createCustomerDocument.mutate(
                                  {
                                    end: new Date(),
                                    agent: '',
                                    allDay: false,
                                    baseColor: '',
                                    color: '',
                                    comment: document.comment ?? '',
                                    date: document?.date ?? new Date(),
                                    description: '',
                                    entBName: '',
                                    entName: '',
                                    id: 0,
                                    issueDate: document?.issueDate ?? new Date(),
                                    mainValue: 0,
                                    name: '',
                                    nkObject: undefined,
                                    status: 'pending_approval',
                                    textColor: '',
                                    title: '',
                                    type: 'visit_note',
                                    ...document,
                                    entId: customer?.id,
                                    products: document.products || [],
                                  },
                              );
                            }
                          })
                        }}
                        loading={createCustomerDocument.isLoading}
                        disabled={createCustomerDocument.isSuccess}
                        variant='contained'
                        type='submit'
                        fullWidth
                        sx={{m: 2}}
                    >
                      הנפק
                    </LoadingButton>
                  </Box>
                </Grid>
              </Grid>
            </SubCard>
       );
};

export default NewVisit;
